/**
 * CR-6348: Supplier section of the reworked SO Lines filter panel.
 * Fields: Country, Group -> Site (Group -> Site cascade preserved).
 */
import type { LucideIcon } from 'lucide-react';
import SearchableSelect from '../../../../../ui/SearchableSelect';
import { POLinesFilters, FilterOptions, SUPPLIER_TYPE_OPTIONS } from '../../types';
import { FilterSection, FIELD_CLS, LABEL_CLS } from '../fieldStyles';
import { useFilterVisibility } from '../../../../../../hooks/useFilterVisibility';

interface SupplierSectionProps {
  filters: POLinesFilters;
  onFilterChange: (key: string, value: string) => void;
  supplierCountries: string[];
  supplierGroups: NonNullable<FilterOptions['supplier_groups']>;
  supplierSiteOptions: FilterOptions['suppliers'];
  onSupplierGroupChange: (value: string) => void;
  onSupplierCountryChange: (value: string) => void;
  /** CR-6347: Optional icon for the section header */
  icon?: LucideIcon;
  /**
   * CR-6347 rework: "Strategic suppliers only" is a per-customer designation
   * (SupplierCustomerRelationship.strategic), so only the customer panel
   * offers it. Defaults off to leave the supplier view untouched.
   */
  showStrategic?: boolean;
  /**
   * CR-9121: "Call-off suppliers only" — the relationship's call_off_enabled
   * flag, so customer panel only, and only where this customer runs call-off
   * at all (the switch_call_off_tab flag). Defaults off.
   */
  showCallOff?: boolean;
  /**
   * CR-6347 rework: Supplier Type (internal / external) is relative to the
   * customer organisation in scope, so only the customer panel offers it.
   * Client feedback places it in the Supplier section. Defaults off.
   */
  showSupplierType?: boolean;
}

export function SupplierSection({
  filters,
  onFilterChange,
  supplierCountries,
  supplierGroups,
  supplierSiteOptions,
  onSupplierGroupChange,
  onSupplierCountryChange,
  icon,
  showStrategic = false,
  showCallOff = false,
  showSupplierType = false,
}: SupplierSectionProps) {
  // CR-4802: filters this tenant shows — one answer for every panel.
  const fv = useFilterVisibility();
  return (
    <FilterSection title="Supplier" icon={icon}>
      {/* Country — CR-6347: now cascades to clear supplier site */}
      <div>
        <label className={LABEL_CLS}>Country</label>
        <select
          value={filters.supplierCountry}
          onChange={(e) => onSupplierCountryChange(e.target.value)}
          className={FIELD_CLS}
        >
          <option value="">All Countries</option>
          {supplierCountries.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>

      {/* Group */}
      <div>
        <label className={LABEL_CLS}>Group</label>
        <SearchableSelect
          options={supplierGroups.map((g) => ({ value: String(g.id), label: g.name }))}
          value={filters.supplierGroup}
          onChange={onSupplierGroupChange}
          placeholder="All Supplier Groups"
        />
      </div>

      {/* Site (cascaded by Group) */}
      <div>
        <label className={LABEL_CLS}>Site</label>
        <SearchableSelect
          options={supplierSiteOptions.map((s) => ({ value: String(s.id), label: s.name }))}
          value={filters.supplier}
          onChange={(val) => onFilterChange('supplier', val)}
          placeholder="All Supplier Sites"
        />
      </div>

      {/* CR-7028: Supplier Code filter — CR-4802: only when the tenant holds supplier codes */}
      {fv.supplierCode && (
      <div>
        <label className={LABEL_CLS}>Supplier Code</label>
        <input
          type="text"
          value={filters.supplierCode}
          onChange={(e) => onFilterChange('supplierCode', e.target.value)}
          placeholder="Enter supplier code"
          className={FIELD_CLS}
        />
      </div>
      )}

      {/* CR-6347 rework: Supplier Type (internal / external) — CR-4802: tenant-declared */}
      {showSupplierType && fv.supplierType && (
        <div>
          <label className={LABEL_CLS}>Supplier Type</label>
          <select
            value={filters.supplierType}
            onChange={(e) => onFilterChange('supplierType', e.target.value)}
            className={FIELD_CLS}
          >
            <option value="">All Suppliers</option>
            {SUPPLIER_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      )}

      {/* CR-6347 rework: Strategic suppliers only — same control and wording
          as the Dashboard / PO Headers Supplier sections. */}
      {showStrategic && (
        <div className="flex items-center pt-7">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.strategicOnly === 'true'}
              onChange={(e) => onFilterChange('strategicOnly', e.target.checked ? 'true' : '')}
              className="w-4 h-4 rounded border-border text-primary focus:ring-primary focus:ring-2"
            />
            <span className="text-sm font-medium">Strategic suppliers only</span>
          </label>
        </div>
      )}

      {/* CR-9121: Call-off suppliers only — same control as Strategic. */}
      {showCallOff && (
        <div className="flex items-center pt-7">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.callOffOnly === 'true'}
              onChange={(e) => onFilterChange('callOffOnly', e.target.checked ? 'true' : '')}
              className="w-4 h-4 rounded border-border text-primary focus:ring-primary focus:ring-2"
            />
            <span className="text-sm font-medium">Call-off suppliers only</span>
          </label>
        </div>
      )}
    </FilterSection>
  );
}
